import { createStore, applyMiddleware } from "redux";
import thunk from "redux-thunk";
import createSagaMiddleware from "redux-saga";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { createMigrate, persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web and AsyncStorage for react-nativeorage for react-native
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import ImmutablePersistenceTransform from "./utils/immutablePersistenceTransform";
import { composeWithDevTools } from "redux-devtools-extension";
import LogRocket from "logrocket";
import history from "./history";
import rootReducer from "./reducers";
import { rehydrationComplete } from "./actions/AppActions";
import createBlacklist from "./utils/createReduxBlacklist";
import Logger from "./utils/logger";
import {
  initializeApiClient,
  initializeShopifyApiClient,
  initializeZendeskApiClient,
} from "./api";
import storeMiddleware from "./utils/middleware";
import sagas from "./sagas";
import migrations from "./utils/migrations";
import { isDevMode } from "./config";

// Constants
const TAG = "store";
const sagaMiddleware = createSagaMiddleware();
const reduxPersistBlacklist = [
  { reducer: "app", key: "fetching" },
  { reducer: "app", key: "errors" },
  { reducer: "app", key: "rehydrationComplete" },
  { reducer: "app", key: "location" },
  { reducer: "app", key: "routeChanged" },
  { reducer: "app", key: "modalWasClosed" },
  { reducer: "tracking", key: "data" },
  { reducer: "vendor", key: "instagram" },
  { reducer: "vendor", key: "saving" },
  { reducer: "vendor", key: "fetching" },
  { reducer: "shop", key: "fetching" },
  { reducer: "shop", key: "cartOpen" },
  { reducer: "shop", key: "quantityUpdates" },
  { reducer: "shop", key: "addingToCart" },
  { reducer: "shop", key: "collections" },
  { reducer: "support", key: "categories.isFetching" },
  { reducer: "support", key: "sections.isFetching" },
  { reducer: "support", key: "articles.isFetching" },
  { reducer: "support", key: "article.isFetching" },
  { reducer: "support", key: "section.isFetching" },
  { reducer: "support", key: "search.isFetching" },
  { reducer: "user", key: "resetRequestSent" },
  { reducer: "user", key: "signupRequestSent" },
  { reducer: "user", key: "hatchForWorkRequestSent" },
  { reducer: "user", key: "sending" },
  { reducer: "user", key: "errors" },
];

const persistConfig = {
  key: "root",
  keyPrefix: "answered:",
  version: 1,
  storage,
  debug: isDevMode,
  transforms: [
    ...createBlacklist(reduxPersistBlacklist),
    ImmutablePersistenceTransform,
  ],
  whitelist: ["app", "vendor", "tracking", "shop", "support", "user"],
  // Needs to be set to level1 explicitly
  // https://github.com/rt2zz/redux-persist-transform-immutable/issues/31
  stateReconciler: autoMergeLevel2,
  // Remove deprecated Answered keys from state
  migrate: createMigrate(migrations, { debug: isDevMode }),
};

export default function configureStore(initialStoreState = {}) {
  const persistedReducer = persistReducer(persistConfig, rootReducer(history));
  const enhancers = [];
  const middlewares = [...storeMiddleware, sagaMiddleware, thunk];

  // https://docs.logrocket.com/reference#redux-logging
  // Note: LogRocket middleware must go last
  if (process.env.REACT_APP_LOG_ROCKET_ID) {
    middlewares.push(LogRocket.reduxMiddleware());
  }

  const composedEnhancers = composeWithDevTools(
    applyMiddleware(routerMiddleware(history), ...middlewares),
    ...enhancers
  );

  // Do we have preloaded state available? Great, save it.
  const initialState = window.__PRELOADED_STATE__ || initialStoreState;

  // Create the store
  const store = createStore(
    connectRouter(history)(persistedReducer),
    initialState,
    composedEnhancers
  );
  const persistor = persistStore(store, null, () => {
    Logger.d(TAG, "Rehydration complete");

    store.dispatch(rehydrationComplete(history));
  });

  if (module.hot) {
    module.hot.accept(() => {
      // This fetch the new state of the above reducers.
      store.replaceReducer(persistReducer(persistConfig, rootReducer(history)));
    });
  }

  sagaMiddleware.run(() => sagas(store));

  initializeApiClient(store);
  initializeShopifyApiClient();
  initializeZendeskApiClient();

  return {
    store,
    history,
    persistor,
  };
}
